/* eslint-disable */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Power, AlertTriangle, X } from "lucide-react";
import {
  deactivateDhdPartnership,
  pauseGoogleSheetForDhd,
  findStoreIdByUserId,
} from "../utils/dhdHelpers";

export const DHD_PLAN_NAME = "شراكة مع dhd";
export const DHD_PLATFORM_URL = "https://platform.dhd-dz.com";

export default function DhdCurrentPlanBanner({
  accountId,
  userId,
  subscription,
  onLocalUpdate,
}) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";
  const [busy, setBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const eligible =
    subscription?.dhdPartnership === true ||
    subscription?.plan === DHD_PLAN_NAME ||
    !!subscription?.previousPlan;

  if (!eligible) return null;

  const isActive =
    subscription?.plan === DHD_PLAN_NAME ||
    subscription?.dhdPartnershipActive === true;

  const handleActivate = async () => {
    if (!accountId || !userId || busy) return;
    setBusy(true);
    try {
      const sub = { ...(subscription || {}) };
      if (sub.plan && sub.plan !== DHD_PLAN_NAME) {
        sub.previousPlan = sub.plan;
      }
      sub.plan = DHD_PLAN_NAME;
      sub.dhdPartnershipActive = true;
      sub.dhdPartnership = true;

      await updateDoc(doc(db, "account", accountId), { subscription: sub });

      try {
        const storeId = await findStoreIdByUserId(userId);
        if (storeId) {
          await pauseGoogleSheetForDhd(storeId);
        }
      } catch (e) {
        console.error("Failed to pause Google Sheet for DHD:", e);
      }

      onLocalUpdate?.(sub);
    } catch (err) {
      console.error("DHD activate failed:", err);
    } finally {
      setBusy(false);
    }
  };

  const handleDeactivateConfirm = async () => {
    if (!accountId || !userId || busy) return;
    setBusy(true);
    try {
      const newSub = await deactivateDhdPartnership(
        userId,
        accountId,
        subscription,
      );
      onLocalUpdate?.(newSub);
      setShowConfirm(false);
    } catch (err) {
      console.error("DHD deactivate failed:", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="dhd-sub-banner" dir={isRTL ? "rtl" : "ltr"}>
        <style>{`
          .dhd-sub-banner {
            position: relative;
            overflow: hidden;
            border-radius: 14px;
            border: 1px solid rgba(255, 106, 0, 0.22);
            background: linear-gradient(120deg, #1a0a00 0%, #2a1200 50%, #1a0a00 100%);
            padding: 1.1rem 1.25rem;
          }
          .dhd-sub-banner__lines {
            position: absolute;
            inset: 0;
            pointer-events: none;
            opacity: 0.35;
            background:
              repeating-linear-gradient(
                -18deg,
                transparent,
                transparent 10px,
                rgba(255, 106, 0, 0.06) 10px,
                rgba(255, 106, 0, 0.06) 11px
              );
          }
          .dhd-sub-banner__row {
            position: relative;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: space-between;
            gap: 0.75rem;
          }
          .dhd-sub-banner__label {
            font-size: 0.72rem;
            font-weight: 700;
            letter-spacing: 0.06em;
            text-transform: uppercase;
            color: rgba(255, 106, 0, 0.85);
            margin-bottom: 0.2rem;
          }
          .dhd-sub-banner__title {
            margin: 0;
            font-size: 1.05rem;
            font-weight: 800;
            color: #fff;
          }
          .dhd-sub-banner__hint {
            margin: 0.25rem 0 0;
            font-size: 0.8rem;
            color: rgba(255, 255, 255, 0.55);
          }
          .dhd-sub-banner__toggle {
            display: inline-flex;
            align-items: center;
            gap: 0.4rem;
            padding: 0.5rem 1rem;
            border-radius: 10px;
            font-size: 0.8rem;
            font-weight: 700;
            border: none;
            cursor: pointer;
            transition: background 0.15s ease, opacity 0.15s ease;
            white-space: nowrap;
          }
          .dhd-sub-banner__toggle:disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }
          .dhd-sub-banner__toggle--on {
            background: #FF6A00;
            color: #1a0a00;
          }
          .dhd-sub-banner__toggle--on:hover:not(:disabled) {
            background: #e55f00;
          }
          .dhd-sub-banner__toggle--off {
            background: rgba(255, 255, 255, 0.1);
            color: rgba(255, 255, 255, 0.9);
            border: 1px solid rgba(255, 255, 255, 0.15);
          }
          .dhd-sub-banner__toggle--off:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.16);
          }
        `}</style>

        <div className="dhd-sub-banner__lines" />
        <div className="dhd-sub-banner__row">
          <div>
            <div className="dhd-sub-banner__label">
              {t("SubscriptionSettings.plans.dhd_partnership.name")}
            </div>
            <h3 className="dhd-sub-banner__title">
              {isActive
                ? t("SubscriptionSettings.dhd_partnership.status_active_short")
                : t(
                    "SubscriptionSettings.dhd_partnership.status_inactive_short",
                  )}
            </h3>
            <p className="dhd-sub-banner__hint">
              {isActive
                ? t("SubscriptionSettings.dhd_partnership.sub_hint_active")
                : t("SubscriptionSettings.dhd_partnership.sub_hint_inactive")}
            </p>
          </div>

          <button
            type="button"
            className={`dhd-sub-banner__toggle ${
              isActive
                ? "dhd-sub-banner__toggle--on"
                : "dhd-sub-banner__toggle--off"
            }`}
            disabled={busy}
            onClick={() => {
              if (isActive) {
                setShowConfirm(true);
              } else {
                handleActivate();
              }
            }}
          >
            <Power size={15} />
            {busy
              ? t("SubscriptionSettings.dhd_partnership.updating")
              : isActive
                ? t("SubscriptionSettings.dhd_partnership.deactivate")
                : t("SubscriptionSettings.dhd_partnership.activate")}
          </button>
        </div>
      </div>

      {showConfirm && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            background: "rgba(15, 5, 25, 0.72)",
            backdropFilter: "blur(6px)",
          }}
          dir={isRTL ? "rtl" : "ltr"}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl"
            style={{ animation: "dhdModalIn 0.22s ease-out" }}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#9b59bf] to-[#6b2d8b] px-5 pt-5 pb-4">
              <button
                type="button"
                onClick={() => !busy && setShowConfirm(false)}
                className="absolute top-4 end-4 w-8 h-8 rounded-full bg-black/20 text-white flex items-center justify-center hover:bg-black/30 transition"
                aria-label={t("video.close")}
              >
                <X size={16} />
              </button>
              <h3 className="text-white text-lg font-bold pe-8">
                {t("SubscriptionSettings.dhd_partnership.confirm_title")}
              </h3>
              <p className="text-white/80 text-sm mt-1">
                {t("SubscriptionSettings.dhd_partnership.confirm_subtitle")}
              </p>
            </div>

            {/* Body */}
            <div className="p-5">
              <div className="flex gap-3 p-3.5 rounded-xl bg-red-50 border border-red-100 mb-5">
                <AlertTriangle
                  size={20}
                  className="text-red-600 shrink-0 mt-0.5"
                />
                <p className="text-sm text-red-800 leading-relaxed">
                  {t("SubscriptionSettings.dhd_partnership.confirm_warning")}
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setShowConfirm(false)}
                  className="flex-1 h-11 rounded-xl font-semibold text-sm bg-gray-100 text-gray-700 hover:bg-gray-200 transition disabled:opacity-60"
                >
                  {t("SubscriptionSettings.dhd_partnership.cancel")}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={handleDeactivateConfirm}
                  className="flex-1 h-11 rounded-xl font-semibold text-sm bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-60"
                >
                  {busy
                    ? t("SubscriptionSettings.dhd_partnership.deactivating")
                    : t(
                        "SubscriptionSettings.dhd_partnership.confirm_deactivate",
                      )}
                </button>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes dhdModalIn {
              from { opacity: 0; transform: scale(0.94) translateY(10px); }
              to   { opacity: 1; transform: scale(1) translateY(0); }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
